/**
 * =============================================================================
 * SPEQ55 RFP Pro - Dashboard Page
 * =============================================================================
 *
 * The Dashboard is the main landing page after login. It provides:
 * 1. Overview of key metrics and KPIs
 * 2. Recent RFPs list
 * 3. Upcoming deadlines
 * 4. Quick actions for common tasks
 *
 * @module pages/Dashboard
 * @author SPEQ55
 */

import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { get } from '../api/client';
import { useAuth } from '../contexts/AuthContext';
import { getMyUsage } from '../api/usageApi';
import UsageMeter from '../components/usage/UsageMeter';

// =============================================================================
// HELPER COMPONENTS
// =============================================================================

/**
 * Stats card component for displaying key metrics.
 */
function StatsCard({ label, value, change, changeType, icon }) {
  const changeColors = {
    positive: 'text-secondary-600 bg-secondary-50',
    negative: 'text-error bg-error-light',
    neutral: 'text-accent-500 bg-accent-100',
  };

  return (
    <div className="card p-6">
      <div className="flex items-start justify-between">
        <div>
          <p className="text-sm font-medium text-accent-500">{label}</p>
          <p className="mt-2 text-3xl font-bold text-accent-800">{value}</p>
        </div>
        <div className="text-3xl">{icon}</div>
      </div>
      {change !== undefined && (
        <div className="mt-4">
          <span
            className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${changeColors[changeType]}`}
          >
            {change}
          </span>
        </div>
      )}
    </div>
  );
}

/**
 * Status badge component for RFP status display.
 */
function StatusBadge({ status }) {
  const statusConfig = {
    draft: { label: 'Draft', className: 'badge bg-accent-100 text-accent-700' },
    tracking: {
      label: 'Tracking',
      className: 'badge bg-info-light text-info-dark',
    },
    no_bid: {
      label: 'No Bid',
      className: 'badge bg-accent-200 text-accent-600',
    },
    in_progress: {
      label: 'In Progress',
      className: 'badge bg-warning-light text-warning-dark',
    },
    submitted: {
      label: 'Submitted',
      className: 'badge bg-secondary-100 text-secondary-700',
    },
    won: {
      label: 'Won',
      className: 'badge bg-success-light text-success-dark',
    },
    lost: { label: 'Lost', className: 'badge bg-error-light text-error-dark' },
    cancelled: {
      label: 'Cancelled',
      className: 'badge bg-accent-200 text-accent-500',
    },
  };

  const config = statusConfig[status] || statusConfig.draft;
  return <span className={config.className}>{config.label}</span>;
}

/**
 * Format date for display.
 */
function formatDate(dateString) {
  if (!dateString) return '-';
  const date = new Date(dateString);
  return date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

/**
 * Get days until deadline and urgency level.
 */
function getDeadlineInfo(dateString) {
  if (!dateString) return { days: null, urgency: 'normal' };

  const deadline = new Date(dateString);
  const now = new Date();
  const diffTime = deadline.getTime() - now.getTime();
  const days = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

  let urgency = 'normal';
  if (days < 0) urgency = 'overdue';
  else if (days <= 7) urgency = 'urgent';
  else if (days <= 14) urgency = 'warning';

  return { days, urgency };
}

// =============================================================================
// DASHBOARD COMPONENT
// =============================================================================

function Dashboard() {
  const navigate = useNavigate();
  const { hasMinRole } = useAuth();

  // State
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [stats, setStats] = useState(null);
  const [recentRFPs, setRecentRFPs] = useState([]);
  const [upcomingDeadlines, setUpcomingDeadlines] = useState([]);
  const [usage, setUsage] = useState(null);

  const canCreateRFP = hasMinRole('org_admin');

  /**
   * Fetch dashboard data from API.
   */
  useEffect(() => {
    const fetchDashboardData = async () => {
      setLoading(true);
      setError(null);

      try {
        // Fetch stats, recent RFPs, upcoming deadlines, and usage in parallel
        const [statsResponse, recentResponse, upcomingResponse, usageData] = await Promise.all([
          get('/rfps/stats'),
          get('/rfps', { limit: 5, sort: '-updated_at' }),
          get('/rfps', {
            status: 'draft,tracking,in_progress',
            sort: 'submission_deadline',
            limit: 5,
          }),
          // Usage is non-critical - resolve to null on error
          getMyUsage().catch(() => null),
        ]);

        setStats(statsResponse.data.stats);
        setRecentRFPs(recentResponse.data.rfps || []);

        // Set usage if available (will be null if fetch failed)
        if (usageData) {
          setUsage(usageData);
        }

        // Filter upcoming deadlines to only show future ones
        const upcoming = (upcomingResponse.data.rfps || []).filter((rfp) => {
          const deadline = new Date(rfp.submissionDeadline);
          return deadline > new Date();
        });
        setUpcomingDeadlines(upcoming.slice(0, 5));
      } catch {
        setError('Failed to load dashboard data. Please try again.');
      } finally {
        setLoading(false);
      }
    };

    fetchDashboardData();
  }, []);

  // Loading state
  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-96">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-primary-200 border-t-primary-600 rounded-full animate-spin mx-auto" />
          <p className="mt-4 text-accent-500">Loading dashboard...</p>
        </div>
      </div>
    );
  }

  // Build stats cards from API data
  const statsCards = stats
    ? [
        {
          id: 'active-rfps',
          label: 'Active RFPs',
          value: stats.active,
          icon: '📄',
        },
      ]
    : [];

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-accent-800">Dashboard</h1>
          <p className="mt-1 text-accent-500">
            Welcome back! Here&apos;s your RFP activity overview.
          </p>
        </div>
        {canCreateRFP && (
          <Link to="/app/rfps/create" className="btn-primary">
            <span className="mr-2">+</span>
            New RFP
          </Link>
        )}
      </div>

      {/* Error Alert */}
      {error && (
        <div className="bg-error-light border-l-4 border-error p-4 rounded">
          <p className="text-error-dark">{error}</p>
        </div>
      )}

      {/* Stats and Usage */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-4xl">
        <div>
          {statsCards.map((stat) => (
            <StatsCard key={stat.id} {...stat} />
          ))}
        </div>
        {usage && (
          <div>
            <UsageMeter count={usage.count} limit={usage.limit} resetsAt={usage.resetsAt} />
          </div>
        )}
      </div>

      {/* Content Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Recent RFPs - Takes 2 columns */}
        <div className="lg:col-span-2">
          <div className="card">
            <div className="card-header flex items-center justify-between">
              <h2 className="text-lg font-semibold text-accent-800">Recent RFPs</h2>
              <Link to="/app/rfps" className="text-sm text-primary-600 hover:text-primary-700">
                View all
              </Link>
            </div>
            <div className="card-body p-0">
              {recentRFPs.length === 0 ? (
                <div className="px-6 py-8 text-center">
                  <div className="text-4xl mb-2">📄</div>
                  <p className="text-accent-500">No RFPs yet</p>
                  {canCreateRFP && (
                    <Link
                      to="/app/rfps/create"
                      className="text-primary-600 hover:text-primary-700 text-sm"
                    >
                      Create your first RFP
                    </Link>
                  )}
                </div>
              ) : (
                <div className="divide-y divide-accent-200">
                  {recentRFPs.map((rfp) => (
                    <div
                      key={rfp.id}
                      onClick={() => navigate(`/app/rfps/${rfp.id}`)}
                      className="px-6 py-4 hover:bg-accent-50 transition-colors cursor-pointer"
                    >
                      <div className="flex items-start justify-between gap-4">
                        <div className="min-w-0">
                          <p className="text-sm font-medium text-accent-800 truncate">
                            {rfp.title}
                          </p>
                          <div className="mt-1 flex items-center gap-3 text-sm text-accent-500">
                            <span className="font-mono">{rfp.rfpNumber}</span>
                            {rfp.companyName && (
                              <>
                                <span>•</span>
                                <span>{rfp.companyName}</span>
                              </>
                            )}
                          </div>
                        </div>
                        <div className="flex flex-col items-end gap-2">
                          <StatusBadge status={rfp.status} />
                          <span className="text-xs text-accent-400">
                            Due {formatDate(rfp.submissionDeadline)}
                          </span>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Quick Actions and Deadlines - 1 column */}
        <div className="space-y-6">
          {/* Quick Actions */}
          <div className="card">
            <div className="card-header">
              <h2 className="text-lg font-semibold text-accent-800">Quick Actions</h2>
            </div>
            <div className="card-body space-y-3">
              {canCreateRFP && (
                <Link to="/app/rfps/create" className="w-full btn-outline justify-start block">
                  <span className="mr-3">📄</span>
                  Create New RFP
                </Link>
              )}
              <Link to="/app/rfps" className="w-full btn-outline justify-start block">
                <span className="mr-3">📋</span>
                View All RFPs
              </Link>
              <button className="w-full btn-outline justify-start" disabled>
                <span className="mr-3">📝</span>
                Start Proposal
                <span className="ml-auto text-xs text-accent-400">(Coming soon)</span>
              </button>
              <button className="w-full btn-outline justify-start" disabled>
                <span className="mr-3">📊</span>
                View Reports
                <span className="ml-auto text-xs text-accent-400">(Coming soon)</span>
              </button>
            </div>
          </div>

          {/* Upcoming Deadlines */}
          <div className="card">
            <div className="card-header">
              <h2 className="text-lg font-semibold text-accent-800">Upcoming Deadlines</h2>
            </div>
            <div className="card-body">
              {upcomingDeadlines.length === 0 ? (
                <p className="text-accent-500 text-sm text-center py-4">No upcoming deadlines</p>
              ) : (
                <div className="space-y-4">
                  {upcomingDeadlines.map((rfp) => {
                    const { days, urgency } = getDeadlineInfo(rfp.submissionDeadline);
                    const urgencyColors = {
                      overdue: 'bg-error-light text-error',
                      urgent: 'bg-error-light text-error',
                      warning: 'bg-warning-light text-warning-dark',
                      normal: 'bg-info-light text-info-dark',
                    };

                    return (
                      <div
                        key={rfp.id}
                        onClick={() => navigate(`/app/rfps/${rfp.id}`)}
                        className="flex items-center gap-3 cursor-pointer hover:opacity-80"
                      >
                        <div
                          className={`w-10 h-10 rounded-lg flex items-center justify-center ${urgencyColors[urgency]}`}
                        >
                          <span className="font-bold text-sm">
                            {days !== null ? (days < 0 ? '!' : days) : '-'}
                          </span>
                        </div>
                        <div className="min-w-0 flex-1">
                          <p className="text-sm font-medium text-accent-800 truncate">
                            {rfp.title}
                          </p>
                          <p className="text-xs text-accent-500">
                            {formatDate(rfp.submissionDeadline)}
                            {days !== null && days >= 0 && ` (${days} day${days !== 1 ? 's' : ''})`}
                            {days !== null && days < 0 && ' (Overdue)'}
                          </p>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default Dashboard;
